import { writable, get } from "svelte/store";
import { getCredentialsForIceServers } from "../services/iceServers";
import { closeSocket, sendSocketMessage } from "./socketStore";
import { clearRoom, getRoom } from "./roomStore";
import { localMediaState, localVideoStore, stopLocalVideoStream } from "./localVideoStore";
import { currentVideoDeviceId, loadVideoDevices, videoDevicesStore } from "../utils/mediaDevices";
import { closeDataChannels, createDataChannel, setupIncomingDataChannel } from "./dataChannelStore";
import { pointerStore } from "./pointerStore";
import { getStunChoice } from "../utils/getStunChoice";
import { remoteAudioStore, remoteVideoStore } from "./remoteVideoStore";
import { isScreenSharingStore } from "./isScreenSharingStore";
import { getWebRTCConfiguration } from "../utils/getWebRTCConfiguration";
import { buildLog } from "../utils/log";
import { ROOM_CONNECTION_STATE } from "../types/Room/RoomConnectionState";
import { closePeerConnectionWithClient } from "./peerConnectionWithClientStore";
import { closePeerConnectionWithExpert, peerConnectionWithExpertStore } from "./peerConnectionWithExpertStore";
import "./toastStore";
export const peerConnectionStore = writable(null);
export const pendingIceStore = writable([]);
export async function startLocalVideo(selectedCamera = null) {
    const pc = get(peerConnectionStore);
    const room = getRoom();
    if (!pc)
        return console.warn("[video] peerConnection not ready");
    const oldStream = get(localVideoStore);
    oldStream === null || oldStream === void 0 ? void 0 : oldStream.getTracks().forEach(track => track.stop());
    pc.getSenders().forEach(sender => pc.removeTrack(sender));
    try {
        const supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
        const constraints = { video: {}, audio: room.audioEnabled === true };
        if (selectedCamera === null && supportedConstraints.facingMode) {
            constraints.video.facingMode = { ideal: "environment" };
            currentVideoDeviceId.set(null);
        }
        else if (selectedCamera && supportedConstraints.deviceId) {
            constraints.video.deviceId = { exact: selectedCamera };
            currentVideoDeviceId.set(selectedCamera);
        }
        if (Object.keys(constraints.video).length === 0)
            constraints.video = true;
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        localVideoStore.set(stream);
        stream.getTracks().forEach(track => pc.addTrack(track, stream));
        await loadVideoDevices();
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        sendSocketMessage({
            room: room.id,
            action: "ICEOffer",
            type: "connection",
            sdp: offer,
        });
        console.log("[video] ICEOffer sent");
    }
    catch (e) {
        console.error("[video] error accessing camera", e);
    }
}
export async function switchLocalVideo() {
    const pc = get(peerConnectionStore);
    const pcWithExpert = get(peerConnectionWithExpertStore);
    const devices = get(videoDevicesStore);
    const current = get(currentVideoDeviceId);
    const oldStream = get(localVideoStore);
    if (!pc)
        return console.warn("[camera] PeerConnection not ready");
    if (!devices || devices.length <= 1)
        return console.log("[camera] Only one camera available");
    let nextIndex = 0;
    if (current) {
        const currentIndex = devices.findIndex(d => d.id === current);
        nextIndex = (currentIndex + 1) % devices.length;
    }
    const nextDevice = devices[nextIndex];
    currentVideoDeviceId.set(nextDevice.id);
    try {
        const newStream = await navigator.mediaDevices.getUserMedia({
            video: { deviceId: { exact: nextDevice.id } },
            audio: false,
        });
        localVideoStore.set(newStream);
        console.log("[camera] Switching to", nextDevice.label);
        pc.getSenders().forEach(sender => {
            var _a;
            if (((_a = sender.track) === null || _a === void 0 ? void 0 : _a.kind) === "video") {
                sender.replaceTrack(newStream.getVideoTracks()[0]);
            }
        });
        if (pcWithExpert) {
            pcWithExpert.getSenders().forEach(sender => {
                var _a;
                if (((_a = sender.track) === null || _a === void 0 ? void 0 : _a.kind) === "video") {
                    sender.replaceTrack(newStream.getVideoTracks()[0]);
                }
            });
        }
        oldStream === null || oldStream === void 0 ? void 0 : oldStream.getTracks().forEach(track => track.stop());
    }
    catch (err) {
        console.error("[camera] Error switching camera:", err);
    }
}
export async function startShareScreen() {
    const pc = get(peerConnectionStore);
    const room = getRoom();
    if (!pc || pc.signalingState !== "stable")
        return console.warn("[shareScreen] signaling not stable");
    const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
    stream.getVideoTracks()[0].onended = () => {
        isScreenSharingStore.set(false);
    };
    localVideoStore.set(stream);
    isScreenSharingStore.set(true);
    stream.getTracks().forEach(track => pc.addTrack(track, stream));
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    sendSocketMessage({
        room: room.id,
        action: "ICEOffer",
        type: "connection",
        sdp: offer,
    });
    console.log("[shareScreen] ICEOffer sent");
}
export async function startScreensharingConseille() {
    const pc = get(peerConnectionStore);
    const room = getRoom();
    if (!pc) {
        console.warn("[video] peerConnection not ready");
        return;
    }
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    sendSocketMessage({ room: room.id, action: "ICEOffer", type: "connection", sdp: offer, });
    console.log("[video] ICEOffer sent");
}
export async function stopShareScreen() {
    const stream = get(localVideoStore);
    if (!stream)
        return;
    stream.getTracks().forEach(track => track.stop());
    isScreenSharingStore.set(false);
}
export async function createPeerConnection(roomId) {
    console.log("[peer] createPeerConnection roomId", roomId);
    const oldPc = get(peerConnectionStore);
    if (oldPc) {
        oldPc.close();
        peerConnectionStore.set(null);
    }
    const credentials = await getCredentialsForIceServers();
    const stunChoice = getStunChoice(roomId);
    const configuration = getWebRTCConfiguration(roomId, credentials);
    const pc = new RTCPeerConnection(configuration);
    buildLog({
        roomId,
        status: ROOM_CONNECTION_STATE.CONNECTING,
        page: "Camera",
        message: `PeerConnection created using server: ${stunChoice}.sd-sfr.fr`
    });
    pc.onicecandidate = (e) => {
        if (e.candidate) {
            sendSocketMessage({
                room: getRoom().id,
                action: "ice",
                type: "candidate",
                label: e.candidate.sdpMLineIndex,
                id: e.candidate.sdpMid,
                candidate: e.candidate.candidate,
            });
        }
    };
    pc.ontrack = (e) => {
        if (e.track.kind === "video") {
            remoteVideoStore.set(new MediaStream([e.track]));
        }
        if (e.track.kind === "audio") {
            remoteAudioStore.set(new MediaStream([e.track]));
        }
    };
    pendingIceStore.set([]);
    createDataChannel(pc, "sendFromMoss");
    setupIncomingDataChannel(pc);
    peerConnectionStore.set(pc);
}
export async function handleOffer(sdp) {
    if (!sdp || !sdp.sdp || !sdp.type) {
        console.warn("handleOffer: invalid SDP", sdp);
        return;
    }
    const pc = get(peerConnectionStore);
    if (!pc)
        return;
    await pc.setRemoteDescription(new RTCSessionDescription(sdp));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    sendSocketMessage({
        room: getRoom().id,
        action: "ICEAnswer",
        type: "connection",
        sdp: answer,
    });
    const pending = get(pendingIceStore);
    for (const c of pending)
        await pc.addIceCandidate(c);
    pendingIceStore.set([]);
}
export async function handleAnswer(sdp) {
    if (!sdp || !sdp.sdp || !sdp.type) {
        console.warn("handleOffer: invalid SDP", sdp);
        return;
    }
    const pc = get(peerConnectionStore);
    if (!pc)
        return;
    await pc.setRemoteDescription(new RTCSessionDescription(sdp));
    const pending = get(pendingIceStore);
    for (const c of pending)
        await pc.addIceCandidate(c);
    pendingIceStore.set([]);
}
export async function addIceCandidate(data) {
    const pc = get(peerConnectionStore);
    if (!pc)
        return;
    const candidate = new RTCIceCandidate({ candidate: data.candidate, sdpMid: data.id, sdpMLineIndex: data.label });
    if (!pc.remoteDescription)
        pendingIceStore.update(l => [...l, candidate]);
    else
        await pc.addIceCandidate(candidate);
}
function closePeerConnection() {
    const pc = get(peerConnectionStore);
    if (!pc)
        return;
    if (pc) {
        pc.getSenders().forEach(sender => { var _a; return (_a = sender.track) === null || _a === void 0 ? void 0 : _a.stop(); });
        pc.close();
        peerConnectionStore.set(null);
    }
}
export function hangupPeerConnection() {
    const room = getRoom();
    if (room === null || room === void 0 ? void 0 : room.id)
        closeSocket(room.id);
    stopLocalVideoStream();
    remoteVideoStore.set(null);
    remoteAudioStore.set(null);
    closeDataChannels();
    closePeerConnection();
    closePeerConnectionWithClient();
    closePeerConnectionWithExpert();
    clearRoom();
    pointerStore.set(null);
    localMediaState.set({
        videoPaused: false,
        videoPauseOrigin: "self",
        audioMuted: false,
        audioMuteOrigin: "self",
    });
}
